import type { NeuronId, SerialNumber } from "~domain/devices/models/common-device-model.ts";
import type { ModulatorCsId, ModulatorHwId } from "~domain/devices/models/modulators/modulator-model.ts";
import type { PilotCsId, PilotHwId } from "~domain/devices/models/pilot/pilot-model.ts";
import type { SiteId } from "~domain/sites/models/site-model.ts";
import type { SubscriberId } from "~domain/subscribers/models/subscriber-model.ts";

import { z } from "zod";

export const searchSiteFieldSchema = z.enum([
  /* ------------- SUBSCRIBER ------------- */
  "subscriberId",
  "subscriberLastname",
  "subscriberFirstname",
  "email",
  "phone",
  "companyName",
  /* ------------- SITE ------------- */
  "siteId",
  "externId",
  "street",
  "postal",
  "town",
  "country",
  "deliveryPoint",
  /* ------------- PILOT ------------- */
  "pilotHwId",
  "pilotCsId",
  "pilotSerial",
  /* ------------- MODULATOR ------------- */
  "modSerial",
  "modHwId",
  "modCsId",
  "modNeuronId",
]);

export const searchSiteFilterTypeSchema = z.enum(["EQUALS", "CONTAINS", "STARTS_BY", "ENDS_BY"]);

export type SearchSiteField = z.infer<typeof searchSiteFieldSchema>;
export type SearchSiteFilterType = z.infer<typeof searchSiteFilterTypeSchema>;

export type SearchSitePayloadItem = {
  field: SearchSiteField;
  filterType: SearchSiteFilterType;
  value: number | string;
};

export type SearchSitesPayload = Array<SearchSitePayloadItem>;

export type SearchSiteResultModulator = {
  csId?: ModulatorCsId;
  hwId?: ModulatorHwId;
  neuronId?: NeuronId;
  serialNumber?: SerialNumber;
};
export type SearchSiteResultPilot = {
  csId?: PilotCsId;
  hwId?: PilotHwId;
};
export type SearchSiteResultSubscriber = {
  companyName?: string;
  emailList: Array<string>;
  phoneList: Array<string>;
  firstname?: string;
  lastname?: string;
  id?: SubscriberId;
};
export type SearchSiteResultPostalAddress = {
  address?: string;
  addressComplement?: string;
  digicode?: string;
  building?: string;
  level?: number;
  stairway?: string;
  cityCode?: string;
  zipCode?: string;
  city?: string;
};

export type SearchSiteResultGeocodedAddress = {
  administrativeDivision: Array<string>;
  cityCode?: string;
  country?: { label: string; isoCode: string };
  geocoding?: { latitude: number; longitude: number };
  inseeCode?: string;
  label?: string;
  street?: string;
  streetNumber?: string;
  city?: string;
  zipCode?: string;
};

export type SearchSiteResult = {
  modulator?: SearchSiteResultModulator;
  pilot?: SearchSiteResultPilot;
  subscriber?: SearchSiteResultSubscriber;
  siteId: SiteId;
  deliveryPoint?: string;
  postalAddress?: SearchSiteResultPostalAddress;
  geocodedAddress?: SearchSiteResultGeocodedAddress;
};
